

import React, { useState, useEffect, useMemo } from 'react';
import { useExchangeStore } from './stores/exchangeStore';
import { usePortfolioStore } from './stores/portfolioStore';
import { getConnectionEntities, getWalletAddresses } from './api/connections';
import { formatSyncDate, parseSyncDate } from './utils/syncDate';




  
const GlobalConnectionStatusHero = ({ onSyncAll, syncAllLoading = false }) => {
  const exchanges = useExchangeStore((s) => s.exchanges);
  const clearAllExchanges = useExchangeStore((s) => s.clearAllExchanges);
  const setOnchainAssets = usePortfolioStore(s => s.setOnchainAssets);
  // Purge handler to remove all exchanges and onchain assets
  // Purge only phantom (inactive) wallets from Zustand
  const handlePurge = async () => {
    clearAllExchanges();
    try {
      const walletList = await getWalletAddresses();
      const nextWallets = Array.isArray(walletList) ? walletList : [];
      console.log('[PURGE DEBUG] getWalletAddresses() returned:', nextWallets);
      setRealWallets(nextWallets);
      setOnchainAssets(nextWallets);
      // Remove persisted Zustand wallet state to fully purge
      try {
        localStorage.removeItem('portfolio-store');
      } catch (e) {}
      window.dispatchEvent(new Event('refresh-connection-sync-state'));
      window.alert('Les connexions fantômes ont été purgées. Rafraîchissez la page si besoin.');
    } catch (e) {
      setRealWallets([]);
      setOnchainAssets([]);
      window.alert('Erreur lors de la purge des wallets.');
    }
  };

  // State for real backend entities
  const [realEntities, setRealEntities] = useState([]);
  const [realWallets, setRealWallets] = useState([]);
  const [syncStateVersion, setSyncStateVersion] = useState(0);
  useEffect(() => {
    const fetchConnectionsOverview = () => {
      Promise.all([getConnectionEntities(), getWalletAddresses()])
        .then(([entities, wallets]) => {
          setRealEntities(Array.isArray(entities) ? entities.filter((entry) => entry.type === 'EXCHANGE') : []);
          setRealWallets(Array.isArray(wallets) ? wallets : []);
        })
        .catch(() => {
          setRealEntities([]);
          setRealWallets([]);
        });
    };
    const handleSyncStateRefresh = () => setSyncStateVersion((value) => value + 1);

    fetchConnectionsOverview();
    // Listen for custom event to refresh entities
    window.addEventListener('refresh-connection-entities', fetchConnectionsOverview);
    window.addEventListener('refresh-connection-sync-state', handleSyncStateRefresh);
    return () => {
      window.removeEventListener('refresh-connection-entities', fetchConnectionsOverview);
      window.removeEventListener('refresh-connection-sync-state', handleSyncStateRefresh);
    };
  }, []);

  // Derive exchange statuses from backend entities so counters survive route changes.
  const exchangeStatusRows = useMemo(() => {
    return realEntities.map((entity) => {
      const id = String(entity.id);
      return {
        id,
        status: exchanges[id] || 'NOT_CONNECTED',
      };
    });
  }, [exchanges, realEntities]);
  // DEBUG LOGGING
  useEffect(() => {
    console.log('[GlobalConnectionStatusHero] Zustand exchanges:', exchanges);
    console.log('[GlobalConnectionStatusHero] realEntities:', realEntities);
    console.log('[GlobalConnectionStatusHero] exchangeStatusRows:', exchangeStatusRows);
  }, [exchanges, exchangeStatusRows, realEntities]);

  
    // Extra debug logging for all intermediate variables
  // (DEBUG LOGGING REMOVED)

  
  // Always recalculate counts on render
  const activeExchanges = exchangeStatusRows.filter((entry) => entry.status === 'CONNECTED').length;
  const totalExchanges = realEntities.length;
  const totalWallets = realWallets.length;
  const activeWallets = totalWallets;
  const totalActive = activeExchanges + activeWallets;
  const totalDisplayed = totalExchanges + totalWallets;
  // Status logic
  let status = 'Tout synchronisé';
  let badgeClass = 'status-ok';
  if (exchangeStatusRows.some((entry) => entry.status === 'ERROR')) {
    status = 'Erreur détectée';
    badgeClass = 'status-error';
  } else if (exchangeStatusRows.some((entry) => entry.status === 'SYNCING')) {
    status = 'Données partielles';
    badgeClass = 'status-partial';
  }
  // Récupère la dernière synchronisation globale
  const [lastSyncGlobal, setLastSyncGlobal] = React.useState('');
  useEffect(() => {
    // Cherche la dernière date de sync de tous les exchanges et wallets
    const syncDates = [];
    realEntities.forEach((entity) => {
      const id = entity?.id;
      if (id == null) {
        return;
      }
      const val = localStorage.getItem('ecotoneai_lastSync_' + id);
      const parsed = parseSyncDate(val);
      if (parsed) syncDates.push(parsed);
    });
    realWallets.forEach((wallet) => {
      const walletId = wallet?.id;
      if (walletId == null) {
        return;
      }
      const val = localStorage.getItem('ecotoneai_lastSync_wallet_' + walletId);
      const parsed = parseSyncDate(val);
      if (parsed) syncDates.push(parsed);
    });
    if (syncDates.length > 0) {
      const maxTime = Math.max(...syncDates.map(d => d.getTime()));
      if (!isNaN(maxTime)) {
        setLastSyncGlobal(formatSyncDate(maxTime));
      } else {
        setLastSyncGlobal('Jamais');
      }
    } else {
      setLastSyncGlobal('Jamais');
    }
  }, [realEntities, realWallets, syncStateVersion]);
  // ...existing code...
  // Affiche le bouton Purger si incohérence détectée
  // Affiche le bouton si au moins un exchange ou wallet existe (pour permettre purge manuelle)
  const shouldShowPurge = (activeExchanges > 0 || activeWallets > 0);
  const shouldShowSyncAll = (totalExchanges > 0 || totalWallets > 0);
  return (
    <div className="global-connection-status-hero">
      <div className="status-title">Statut global des connexions</div>
      <div className="status-info">
        {totalActive} connexions synchronisées / {totalDisplayed} affichées — Synchronisation {lastSyncGlobal}
      </div>
      <div className="status-actions">
        <span className={`status-badge ${badgeClass}`}>{status}</span>
        {shouldShowSyncAll && (
          <button className="primary-action-button" onClick={onSyncAll} disabled={syncAllLoading}>
            {syncAllLoading ? 'Actualisation...' : 'Actualiser tout'}
          </button>
        )}
        {shouldShowPurge && (
          <button className="danger-action-button" onClick={handlePurge}>
            Purger les connexions fantômes
          </button>
        )}
      </div>
    </div>
  );
};

export default GlobalConnectionStatusHero;
